import React from 'react'
import { PageSpeedDigest, Audit, AuditScore } from './common'
import { ScoreValue } from './ScoreValue'
import { TimeValue } from './TimeValue'
import { FloatValue } from './FloatValue'
import { FasterValue } from './FasterValue'

export type DynamicTableColumns = {
  total?: boolean
  value?: boolean
  score?: boolean
  weight?: boolean
  allocation?: boolean
  description?: boolean
  valueAndScore?: boolean
  causes?: boolean
  plan50?: boolean
  plan90?: boolean
  plan100?: boolean
}

interface Props {
  digest: PageSpeedDigest
  columns: DynamicTableColumns
}

interface State {
}

export class DynamicReportTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
    }
  }

  renderScorePlanColumn(auditScore: AuditScore, targetScore: number, targetValue: number, className = 'text-center nowrap', isTime = true) {
    const digest = this.props.digest
    const total = Object.values(digest.scores).reduce((sum, score) => sum + score.weight, 0)
    const maxScore = Math.max(auditScore.score, targetScore)

    return (
      <td className={`${className} ${PageSpeedDigest.scoreCssClass(maxScore)}`}>
        {auditScore.score >= targetScore ?
          <div className="score-plan-ok">
            <div className="score-plan-ok-mark mb-2">○</div>
            <ScoreValue value={auditScore.score * auditScore.weight / total} max={auditScore.weight / total} digits={2}></ScoreValue>
          </div>
          :
          <>
            <div className="score-plan">
              {isTime ? <><TimeValue ms={auditScore.value}></TimeValue>→<TimeValue ms={targetValue}></TimeValue></> : <><FloatValue value={auditScore.value}></FloatValue>→<FloatValue value={targetValue}></FloatValue></>}
            </div>
            <div className="score-plan-conclusion">
              {isTime ? <><TimeValue ms={targetValue - auditScore.value}></TimeValue> <small>短縮</small></> : <><FloatValue value={targetValue - auditScore.value}></FloatValue> <small>改善</small></>}
            </div>
            {isTime && <div className="score-plan-faster">
              ( <FasterValue faster={auditScore.value / targetValue}></FasterValue> )
            </div>}
            <div>↓</div>
            <div><ScoreValue value={targetScore * auditScore.weight / total} max={auditScore.weight / total} digits={2}></ScoreValue></div>
            <div className="score-plan-plus">
              ( <ScoreValue value={(targetScore - auditScore.score) * auditScore.weight / total} digits={2} plus={true}></ScoreValue> )
            </div>
          </>
        }
      </td>
    )
  }

  renderScorePlanTotalColumn(target: number) {
    const digest = this.props.digest
    const total = Object.values(digest.scores).reduce((sum, score) => sum + score.weight, 0)
    const allocations = Audit.all.map(audit => {
      const auditScore = digest.scores[audit.id]
      const score = Math.max(auditScore.score, target)
      return score * auditScore.weight / total
    })

    const value = allocations.reduce((sum, a) => sum + a, 0)

    return (
      <th className={`text-center ${PageSpeedDigest.scoreCssClass(value)}`}>
        <ScoreValue value={value} max={1}></ScoreValue>
      </th>
    )
  }

  public render() {
    const digest = this.props.digest
    const total = Object.values(digest.scores).reduce((sum, score) => sum + score.weight, 0)

    return (
      <div className="dynamic-report-score mt-3">
        <table className="table table-bordered table-striped table-middle mb-4">
          <thead>
            <tr>
              <th>科目</th>
              {this.props.columns.value && <th className="text-center nowrap">測定結果</th>}
              {this.props.columns.score && <th className="text-center nowrap">単体スコア</th>}
              {this.props.columns.weight && <th className="text-center nowrap">重み</th>}
              {this.props.columns.allocation && <th className="text-center nowrap">配点</th>}
              {this.props.columns.causes && <th className="text-left nowrap">影響を与える主な要因</th>}
              {this.props.columns.plan50 && <th className="text-center nowrap">プラン50</th>}
              {this.props.columns.plan90 && <th className="text-center nowrap">プラン90</th>}
              {this.props.columns.plan100 && <th className="text-center nowrap">プラン100</th>}
            </tr>
          </thead>
          <tbody>
            {Audit.all.map(audit => {
              const score = digest.scores[audit.id]
              return (
                <tr>
                  <th>
                    {audit.important ? <span className="mr-2" style={{ fontSize: '120%' }}><span role="img">✅</span> {audit.name}</span> : <span className="mr-2">{audit.name}</span>}
                    <span className={`badge badge-${audit.b4Color}`}>{audit.group}</span>
                    {this.props.columns.description && <div className="description mt-2">{audit.description}</div>}
                  </th>
                  {this.props.columns.value && <td className="text-center nowrap">{audit.isTime ? <TimeValue ms={score.value}></TimeValue> : <FloatValue value={score.value}></FloatValue>}</td>}
                  {this.props.columns.score && <td className={`text-center nowrap ${PageSpeedDigest.scoreCssClass(score.score)}`}>
                    <ScoreValue value={score.score} max={1}></ScoreValue>
                  </td>}
                  {this.props.columns.weight && <td className="text-center nowrap">{score.weight}</td>}
                  {this.props.columns.allocation && <td className={`text-center nowrap ${PageSpeedDigest.scoreCssClass(score.score)}`}>
                    <ScoreValue value={score.score * score.weight / total} max={score.weight / total} digits={2}></ScoreValue>
                  </td>}
                  {this.props.columns.causes && <td className="text-left nowrap">
                    <ul className="causes">
                      {audit.causes.map(cause => <li>{cause}</li>)}
                    </ul>
                  </td>}
                  {this.props.columns.plan50 && this.renderScorePlanColumn(score, 0.5, score.targets.for50, 'text-center nowrap', audit.isTime)}
                  {this.props.columns.plan90 && this.renderScorePlanColumn(score, 0.9, score.targets.for90, 'text-center nowrap', audit.isTime)}
                  {this.props.columns.plan100 && this.renderScorePlanColumn(score, 1.0, score.targets.for100, 'text-center nowrap', audit.isTime)}
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            {this.props.columns.total && <tr>
              <th>合計</th>
              {this.props.columns.value && <th className="text-center"></th>}
              {this.props.columns.score && <th className="text-center"></th>}
              {this.props.columns.weight && <th className="text-center">{total}</th>}
              {this.props.columns.allocation && <th className={`text-center  ${PageSpeedDigest.scoreCssClass(digest.score)}`}>
                <ScoreValue value={this.props.digest.score} max={1}></ScoreValue>
              </th>}
              {this.props.columns.causes && <th className="text-left"></th>}
              {this.props.columns.plan50 && this.renderScorePlanTotalColumn(0.5)}
              {this.props.columns.plan90 && this.renderScorePlanTotalColumn(0.9)}
              {this.props.columns.plan100 && this.renderScorePlanTotalColumn(1.0)}
            </tr>}
          </tfoot>
        </table>
      </div >

    )
  }
}